import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import { HomestayCard } from "@/components/HomestayCard";
import { homestayAPI, facilitiesAPI } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Filter, X, Loader2, IndianRupee } from "lucide-react";

// Price range options
const priceRanges = [
  { label: "All Prices", value: "all", min: null, max: null },
  { label: "Under ₹2,000", value: "0-2000", min: 0, max: 2000 },
  { label: "₹2,000 - ₹5,000", value: "2000-5000", min: 2000, max: 5000 },
  { label: "₹5,000 - ₹10,000", value: "5000-10000", min: 5000, max: 10000 },
  { label: "₹10,000 - ₹20,000", value: "10000-20000", min: 10000, max: 20000 },
  { label: "Above ₹20,000", value: "20000+", min: 20000, max: null },
];

const ListingsPage = () => {
  const [homestays, setHomestays] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFacility, setSelectedFacility] = useState("");
  const [mealIncluded, setMealIncluded] = useState(false);
  const [priceRange, setPriceRange] = useState("all");
  // Custom price inputs (allow any numeric value)
  const [customMinPrice, setCustomMinPrice] = useState("");
  const [customMaxPrice, setCustomMaxPrice] = useState("");

  useEffect(() => {
    fetchFacilities();
    fetchHomestays();
  }, []);

  const fetchFacilities = async () => {
    try {
      const response = await facilitiesAPI.getAll();
      setFacilities(response.data.facilities);
    } catch (error) {
      console.error("Error fetching facilities:", error);
    }
  };

  const fetchHomestays = async (params = {}) => {
    setLoading(true);
    try {
      const response = await homestayAPI.getAll(params);
      setHomestays(response.data);
    } catch (error) {
      console.error("Error fetching homestays:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (selectedFacility && selectedFacility !== "all") params.facility = selectedFacility;
    if (mealIncluded) params.meal_included = true;
    
    // Custom price range takes priority over preset ranges
    if (customMinPrice || customMaxPrice) {
      if (customMinPrice) params.min_price = parseFloat(customMinPrice);
      if (customMaxPrice) params.max_price = parseFloat(customMaxPrice);
    } else if (priceRange && priceRange !== "all") {
      // Fallback to preset ranges if no custom price
      const range = priceRanges.find(r => r.value === priceRange);
      if (range) {
        if (range.min !== null) params.min_price = range.min;
        if (range.max !== null) params.max_price = range.max;
      }
    }
    
    fetchHomestays(params);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedFacility("");
    setMealIncluded(false);
    setPriceRange("all");
    setCustomMinPrice("");
    setCustomMaxPrice("");
    fetchHomestays();
  };

  const hasActiveFilters = searchQuery || selectedFacility || mealIncluded || (priceRange && priceRange !== "all") || customMinPrice || customMaxPrice;

  // SEO: Update meta tags directly in DOM
  useEffect(() => {
    const metaTitle = 'All Homestays | Browse & Book';
    const metaDescription = 'Browse all homestays available in Kokan region. Filter by price, facilities, meals included, and location to find your perfect stay.';
    const canonicalUrl = `${window.location.origin}/listings`;
    
    document.title = metaTitle;
    
    const updateMeta = (selector, content, isProperty = false) => {
      let meta = document.querySelector(selector);
      if (meta) {
        meta.content = content;
      } else {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
        } else {
          meta.name = selector.match(/name="([^"]+)"/)?.[1] || '';
        }
        meta.content = content;
        document.head.appendChild(meta);
      }
    };
    
    updateMeta('meta[name="description"]', metaDescription);
    updateMeta('meta[name="robots"]', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;
    
    updateMeta('meta[property="og:type"]', 'website', true);
    updateMeta('meta[property="og:url"]', canonicalUrl, true);
    updateMeta('meta[property="og:title"]', metaTitle, true);
    updateMeta('meta[property="og:description"]', metaDescription, true);
    updateMeta('meta[property="og:site_name"]', 'Kokan Homestays', true);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="listings-page">
      <Navbar />
      <PageSEO title="Homestays" description="Browse and book premium homestays across the Konkan coast. Filter by location, price, and amenities." path="/listings" />

      {/* Header */}
      <section className="pt-28 pb-8 px-6 md:px-12" data-testid="listings-header">
        <div className="max-w-7xl mx-auto">
          <h1
            className="text-4xl md:text-5xl font-medium text-foreground mb-4 animate-fade-up"
           
          >
            All Homestays
          </h1>
          <p className="text-lg text-muted-foreground animate-fade-up delay-100">
            Discover unique accommodations for your next adventure
          </p>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="pb-8 px-6 md:px-12" data-testid="search-section">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl p-6 shadow-soft animate-fade-up delay-200">
            {/* Main Search */}
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Search by name, location, or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-12 h-12 rounded-xl border-border"
                  data-testid="search-input"
                />
              </div>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="h-12 px-6 rounded-xl md:hidden"
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <Button
                onClick={handleSearch}
                className="h-12 px-8 rounded-xl bg-primary hover:bg-primary/90"
                data-testid="search-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>

            {/* Filters */}
            <div className={`${showFilters ? "block" : "hidden"} md:block`}>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4 pt-4 border-t">
                {/* Facility Filter */}
                <div className="w-full md:w-48">
                  <Select
                    value={selectedFacility}
                    onValueChange={setSelectedFacility}
                  >
                    <SelectTrigger className="h-11 rounded-lg" data-testid="facility-filter">
                      <SelectValue placeholder="All Facilities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Facilities</SelectItem>
                      {facilities.map((facility) => (
                        <SelectItem key={facility} value={facility}>
                          {facility}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Range Filter */}
                <div className="w-full md:w-52">
                  <Select
                    value={priceRange}
                    onValueChange={(val) => {
                      setPriceRange(val);
                      // Clear custom prices when using preset
                      if (val !== "custom") {
                        setCustomMinPrice("");
                        setCustomMaxPrice("");
                      }
                    }}
                  >
                    <SelectTrigger className="h-11 rounded-lg" data-testid="price-filter">
                      <div className="flex items-center gap-2">
                        <IndianRupee className="w-4 h-4 text-muted-foreground" />
                        <SelectValue placeholder="Price Range" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {priceRanges.map((range) => (
                        <SelectItem key={range.value} value={range.value}>
                          {range.label}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Price Inputs - Show when "Custom Range" selected or when custom values exist */}
                {(priceRange === "custom" || customMinPrice || customMaxPrice) && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Min ₹"
                      value={customMinPrice}
                      onChange={(e) => {
                        setCustomMinPrice(e.target.value);
                        if (e.target.value) setPriceRange("custom");
                      }}
                      className="w-24 h-11"
                      min="0"
                      data-testid="min-price-input"
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input
                      type="number"
                      placeholder="Max ₹"
                      value={customMaxPrice}
                      onChange={(e) => {
                        setCustomMaxPrice(e.target.value);
                        if (e.target.value) setPriceRange("custom");
                      }}
                      className="w-24 h-11"
                      min="0"
                      data-testid="max-price-input"
                    />
                  </div>
                )}

                {/* Meals Included */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="mealIncluded"
                    checked={mealIncluded}
                    onCheckedChange={setMealIncluded}
                    data-testid="meal-filter"
                  />
                  <label htmlFor="mealIncluded" className="text-sm font-medium cursor-pointer">
                    Meals Included
                  </label>
                </div>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="pb-20 px-6 md:px-12" data-testid="results-section">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : homestays.length > 0 ? (
            <>
              <p className="text-muted-foreground mb-8 animate-fade-up">
                {homestays.length} homestay{homestays.length !== 1 ? "s" : ""} found
                {priceRange && priceRange !== "all" && priceRange !== "custom" && (
                  <span className="ml-2 text-primary font-medium">
                    • {priceRanges.find(r => r.value === priceRange)?.label}
                  </span>
                )}
                {(customMinPrice || customMaxPrice) && (
                  <span className="ml-2 text-primary font-medium">
                    • ₹{customMinPrice || '0'} - ₹{customMaxPrice || '∞'}
                  </span>
                )}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                {homestays.map((homestay, index) => (
                  <div
                    key={homestay.id}
                    className="animate-fade-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <HomestayCard homestay={homestay} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20 animate-fade-up">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3
                className="text-2xl font-semibold text-foreground mb-3"
               
              >
                No Homestays Found
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                We couldn't find any homestays matching your criteria. Try adjusting your filters or search query.
              </p>
              <Button
                variant="outline"
                onClick={handleClearFilters}
                className="rounded-full"
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ListingsPage;
